
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchEmployeeUtilization } from "./services_1/api";
import "./App.css";

const API = process.env.REACT_APP_API;
const ALL_PROJECTS_ID = "all";
const ALL_EMPLOYEES_ID = "all"; // New constant for Employee filter

const getProjectId = (project) => project?.id || project?.projectId;
const getProjectName = (project) => project?.name || project?.projectName || "Unknown Project";

const getAvailableMonths = (data) => {
  if (!data || Array.isArray(data) || typeof data !== "object") return [];
  return Object.keys(data)
    .filter((monthKey) => Array.isArray(data[monthKey]))
    .sort()
    .reverse();
};

const getEmployeesForMonth = (data, selectedMonth) => {
  if (Array.isArray(data)) return data;
  if (!data || !selectedMonth) return [];
  return Array.isArray(data[selectedMonth]) ? data[selectedMonth] : [];
};

const formatMonthLabel = (monthKey) => {
  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 1);
  if (Number.isNaN(date.getTime())) return monthKey;
  return date.toLocaleString("default", { month: "long", year: "numeric" });
};

const getEmployeeProjects = (employee) => {
  if (Array.isArray(employee?.projects)) return employee.projects;
  if (employee?.projects && typeof employee.projects === "object") {
    return Object.values(employee.projects);
  }
  return [];
};

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const roundToTwo = (value) => Number(value.toFixed(2));

// UPDATED: Now accepts selectedEmployeeId
const normalizeUtilizationData = (data, selectedProjectId, selectedEmployeeId, projects) => {
  const source = data?.metrics || data?.summary || {};
  const rawEmployees = Array.isArray(data)
    ? data
    : Array.isArray(data?.employees)
    ? data.employees
    : Array.isArray(data?.rows)
      ? data.rows
      : Array.isArray(data?.data)
        ? data.data
        : [];

  // 1. First, filter by the selected employee (if not "all")
  const filteredEmployeesByActiveSelection = selectedEmployeeId === ALL_EMPLOYEES_ID
    ? rawEmployees
    : rawEmployees.filter(emp => String(emp.id) === String(selectedEmployeeId));

  // 2. Then, map and filter their projects
  const employees = filteredEmployeesByActiveSelection
    .map((employee) => {
      const employeeProjects = getEmployeeProjects(employee);
      
      const visibleProjects =
        selectedProjectId === ALL_PROJECTS_ID
          ? employeeProjects
          : employeeProjects.filter(
              (project) => String(project.projectId || project.id) === String(selectedProjectId)
            );

      // If the employee has no visible projects after filtering, exclude them
      if (!visibleProjects.length) return null;

      const selectedProjectUtilization =
        selectedProjectId === ALL_PROJECTS_ID
          ? toNumber(employee.totalUtilization ?? employee.averageUtilization ?? employee.avgUtilization)
          : toNumber(visibleProjects[0]?.projectUtilization);

      return {
        id: employee.id,
        name: employee.name || employee.employeeName || "Unknown Employee",
        projects: visibleProjects,
        averageUtilization: selectedProjectUtilization,
        totalHours:
          selectedProjectId === ALL_PROJECTS_ID
            ? employee.totalHoursAllProjects
            : visibleProjects[0]?.loggedHours
      };
    })
    .filter(Boolean); // Removes null entries

  const utilizationValues = employees
    .map((employee) => employee.averageUtilization)
    .filter((value) => value !== null);

  const projectIds = new Set(
    employees.flatMap((employee) =>
      employee.projects.map((project) => String(project.projectId || project.id)).filter(Boolean)
    )
  );

  const averageUtilization = utilizationValues.length
    ? roundToTwo(utilizationValues.reduce((sum, value) => sum + value, 0) / utilizationValues.length)
    : null;

  return {
    totalEmployees: employees.length, // Dynamically count visible employees
    projectCount: projectIds.size,    // Dynamically count visible projects
    averageUtilization: averageUtilization,
    overAllocated: employees.filter((employee) => employee.averageUtilization > 100).length,
    underutilized: employees.filter((employee) => employee.averageUtilization < 80).length,
    employees
  };
};

const formatMetric = (value, fallback = "--") => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  if (typeof value !== "number") return value;
  return `${Number.isInteger(value) ? value : value.toFixed(2).replace(/\.?0+$/, "")}%`;
};

// Helper to determine row color based on utilization
const getRowStyle = (utilization) => {
  const val = Number(utilization);
  if (isNaN(val)) return {}; // Fallback if no data
  
  // You can adjust these hex codes to match your brand's specific shades
  if (val >= 80) return { backgroundColor: "#2e7d32", color: "white" }; // Green
  if (val >= 50) return { backgroundColor: "#1976d2", color: "white" }; // Blue
  return { backgroundColor: "#d32f2f", color: "white" };                // Red
};

// Generic Searchable Select Component to handle both Projects and Employees
const SearchableSelect = ({ options, value, onChange, disabled, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    const selectedOption = options.find((opt) => String(opt.id) === String(value));
    setSearchTerm(selectedOption ? selectedOption.name : "");
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        const selectedOption = options.find((opt) => String(opt.id) === String(value));
        setSearchTerm(selectedOption ? selectedOption.name : "");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [options, value]);

  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="employee-utilization-select" ref={wrapperRef}>
      <div
        className="employee-utilization-select__button"
        onClick={() => !disabled && setIsOpen(true)}
      >
        <input
          type="text"
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={disabled ? "Loading..." : placeholder}
          disabled={disabled}
        />
        <button
          type="button"
          className="employee-utilization-select__chevron"
          onClick={() => setIsOpen((current) => !current)}
          disabled={disabled}
        />
      </div>

      {isOpen && !disabled && (
        <ul className="employee-utilization-select__menu">
          {filteredOptions.length ? (
            filteredOptions.map((opt) => (
              <li
                key={opt.id}
                onMouseDown={() => {
                  onChange(opt.id);
                  setIsOpen(false);
                }}
                className={String(opt.id) === String(value) ? "selected" : ""}
              >
                {opt.name}
              </li>
            ))
          ) : (
            <li className="employee-utilization-select__empty">No matches found</li>
          )}
        </ul>
      )}
    </div>
  );
};

function EmployeeUtilization() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  
  // States for our three toggles
  const [selectedProject, setSelectedProject] = useState(ALL_PROJECTS_ID);
  const [selectedEmployee, setSelectedEmployee] = useState(ALL_EMPLOYEES_ID);
  const [selectedMonth, setSelectedMonth] = useState("");
  
  const [utilizationData, setUtilizationData] = useState(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoadingUtilization, setIsLoadingUtilization] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const res = await fetch(`${API}/api/projects`, { cache: "no-store" });
        const data = await res.json();
        setProjects(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load projects", error);
        setProjects([]);
      } finally {
        setIsLoadingProjects(false);
      }
    };
    loadProjects();
  }, []);

  useEffect(() => {
    const loadUtilization = async () => {
      setIsLoadingUtilization(true);
      setErrorMessage("");
      try {
        const data = await fetchEmployeeUtilization();
        setUtilizationData(data);
      } catch (error) {
        console.error("Failed to load employee utilization", error);
        setUtilizationData(null);
        setErrorMessage("Employee utilization data is not available yet.");
      } finally {
        setIsLoadingUtilization(false);
      }
    };
    loadUtilization();
  }, []);

  const availableMonths = useMemo(() => getAvailableMonths(utilizationData), [utilizationData]);

  useEffect(() => {
    if (availableMonths.length && !availableMonths.includes(selectedMonth)) {
      setSelectedMonth(availableMonths[0]);
    }
  }, [availableMonths, selectedMonth]);

  const selectedMonthEmployees = getEmployeesForMonth(utilizationData, selectedMonth);

  // Format Project options for the reusable select component
  const projectOptions = useMemo(() => [
    { id: ALL_PROJECTS_ID, name: "All Projects" },
    ...projects.map(p => ({ id: getProjectId(p), name: getProjectName(p) }))
  ], [projects]);

  // Extract unique employees from the CURRENT month to populate the Employee dropdown
  const employeeOptions = useMemo(() => {
    const uniqueEmployees = selectedMonthEmployees.map(emp => ({
      id: emp.id,
      name: emp.name || emp.employeeName || "Unknown Employee"
    }));
    return [
      { id: ALL_EMPLOYEES_ID, name: "All Employees" },
      ...uniqueEmployees
    ];
  }, [selectedMonthEmployees]);

  // Apply both filters
  const metrics = normalizeUtilizationData(selectedMonthEmployees, selectedProject, selectedEmployee, projects);

  // 1. Define booleans to check if we should show the columns
  const showEmployeeColumn = selectedEmployee === ALL_EMPLOYEES_ID;
  const showProjectColumn = selectedProject === ALL_PROJECTS_ID;

  // 2. Dynamically calculate the CSS grid columns based on what is visible
  const columnFractions = [];
  if (showEmployeeColumn) columnFractions.push("1.5fr"); // Employee column width
  if (showProjectColumn) columnFractions.push("2fr");    // Project column width
  columnFractions.push("1fr", "1fr");                    // Hours and Avg Utilization widths
  
  const gridStyle = { 
    gridTemplateColumns: columnFractions.join(" ") 
  };

  return (
    <main className="employee-utilization-page">
      <button type="button" className="employee-utilization-back" onClick={() => navigate("/")}>
        Back to Landing
      </button>

      <section className="employee-utilization-canvas">
        <h1>Employee Utilization Dashboard</h1>

        <div className="employee-utilization-top-row" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <SearchableSelect
            options={projectOptions}
            value={selectedProject}
            onChange={setSelectedProject}
            disabled={isLoadingProjects}
            placeholder="All Projects"
          />

          <SearchableSelect
            options={employeeOptions}
            value={selectedEmployee}
            onChange={setSelectedEmployee}
            disabled={isLoadingUtilization}
            placeholder="All Employees"
          />

          <select
            className="employee-utilization-month-select"
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            disabled={isLoadingUtilization || !availableMonths.length}
          >
            {availableMonths.length ? (
              availableMonths.map((monthKey) => (
                <option key={monthKey} value={monthKey}>
                  {formatMonthLabel(monthKey)}
                </option>
              ))
            ) : (
              <option value="">No Months</option>
            )}
          </select>
        </div>

        <div className="employee-utilization-stats">
          <article className="employee-utilization-stat">
            <span>No of Employee</span>
            <strong>{formatMetric(metrics.totalEmployees)}</strong>
            <span>Across {formatMetric(metrics.projectCount)} Projects</span>
          </article>
          <article className="employee-utilization-stat">
            <span>Avg. Utilization</span>
            <strong>{formatPercent(metrics.averageUtilization)}</strong>
          </article>
          <article className="employee-utilization-stat">
            <span>Over Allocated</span>
            <strong>{formatMetric(metrics.overAllocated)} Resources</strong>
            <span>( Utilization &gt;100% )</span>
          </article>
          <article className="employee-utilization-stat">
            <span>Underutilized</span>
            <strong>{formatMetric(metrics.underutilized)} Resources</strong>
            <span>( Utilization &lt;80% )</span>
          </article>
        </div>

        {/* 3. Apply the dynamic grid style to the table section */}
        <section className="employee-utilization-table" style={gridStyle}>
          
          {/* Conditionally Render Headers */}
          {showEmployeeColumn && <div className="employee-utilization-table__head">Employee</div>}
          {showProjectColumn && <div className="employee-utilization-table__head">Projects</div>}
          <div className="employee-utilization-table__head">Logged Hours</div>
          <div className="employee-utilization-table__head">Avg. Utilization</div>

          {metrics.employees.length ? (
            metrics.employees.flatMap((employee) => {
              
              const showProjectBreakdown = !showEmployeeColumn && showProjectColumn;

              // BREAKDOWN VIEW (Single Employee, All Projects)
              if (showProjectBreakdown) {
                const projectRows = employee.projects.map((project) => {
                  const projStyle = getRowStyle(project.projectUtilization);
                  
                  return (
                    <React.Fragment key={`${employee.id}-${project.projectId || project.id}-${selectedMonth}`}>
                      {/* 
                        FIX: Removed the empty <div> here! 
                        Since the Employee column is hidden, the grid only expects 3 items per row. 
                      */}
                      <div style={projStyle}>{project.projectName || project.name || "Unknown Project"}</div>
                      <div style={projStyle}>
                        {project.loggedHours != null ? `${roundToTwo(project.loggedHours)} hrs` : "--"}
                      </div>
                      <div style={projStyle}>{formatPercent(toNumber(project.projectUtilization))}</div>
                    </React.Fragment>
                  );
                });

                // FIX: Removed the color styling from the total row and updated the border
                const totalRowStyle = { 
                  fontWeight: "700", 
                  borderTop: "2px solid #cfcfcf", 
                  paddingTop: "12px",
                  paddingBottom: "12px",
                  color: "#171717",
                  backgroundColor: "#ffffff"
                };

                const totalRow = (
                  <React.Fragment key={`${employee.id}-total-${selectedMonth}`}>
                    <div style={totalRowStyle}>
                      {employee.name} (Total Assigned Projects)
                    </div>
                    <div style={totalRowStyle}>
                      {employee.totalHours != null ? `${roundToTwo(employee.totalHours)} hrs` : "--"}
                    </div>
                    <div style={totalRowStyle}>
                      {formatPercent(employee.averageUtilization)}
                    </div>
                  </React.Fragment>
                );

                return [...projectRows, totalRow];
              }

              // DEFAULT AGGREGATED VIEW
              const rowStyle = getRowStyle(employee.averageUtilization);
              
              return (
                <React.Fragment key={`${employee.id}-${selectedProject}-${selectedMonth}`}>
                  {/* Conditionally render cells and apply the background/text color */}
                  {showEmployeeColumn && (
                    <div style={rowStyle}>{employee.name || "--"}</div>
                  )}
                  
                  {showProjectColumn && (
                    <div style={rowStyle}>
                      {employee.projects.length
                        ? employee.projects
                            .map((project) => project.projectName || project.name || "Unknown Project")
                            .join(", ")
                        : "--"}
                    </div>
                  )}

                  <div style={rowStyle}>
                    {employee.totalHours != null ? `${roundToTwo(employee.totalHours)} hrs` : "--"}
                  </div>
                  <div style={rowStyle}>
                    {formatPercent(employee.averageUtilization)}
                  </div>
                </React.Fragment>
              );
            })
          ) : (
            <div className="employee-utilization-table__empty" style={{ gridColumn: "1 / -1" }}>
              {isLoadingUtilization
                ? "Loading employee utilization data..."
                : errorMessage || "No data matches your specific filters."}
            </div>
          )}
        </section>
      </section>
    </main>
  );
}

export default EmployeeUtilization;
